import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Button } from '@insurancemenu/imenu-components-lib';
import { useRootStore } from 'store/rootStore';
import ActionModal from 'components/common/modals/ActionModal';
import { uwRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { Feature } from 'types/domainServiceV2';
import { createStyles } from './styles';

const ReviewSubmitFooter = observer((): JSX.Element => {
  const { periodId } = useParams<{ periodId: string }>();
  const history = useHistory();

  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const {
    domainStoreV2: { domainHeader },
    userStore: { userRoles },
    reviewSubmitStore: {
      loading,
      isSubmitNotEligible,
      isElectionValid,
      isFinalRatesMissing,
      enrollmentPeriodCannotBeResubmitted,
      isAlreadySubmitted,
      submitEnrollmentPeriod,
    },
  } = useRootStore();

  const isUwRights = uwRights(userRoles);

  const completeEnrollmentPeriod = async (): Promise<void> => {
    await submitEnrollmentPeriod();
    setSubmitModalOpen(false);
    if (domainHeader.properties.availableFeatures?.includes(Feature.POST_SUBMIT_CHECKLIST)) {
      history.push(`/post-submit-checklist/${periodId}`);
    } else {
      history.push(`/checklist/${periodId}`);
    }
  };

  const classes = createStyles();

  return (
    <>
      {!loading && (
        <>
          <div className={classes.buttonsWrapper}>
            {enrollmentPeriodCannotBeResubmitted ? (
              <>
                <Button onClick={(): void => history.goBack()} type="outlined" size="large" width="24rem">
                  Exit
                </Button>
                {isUwRights && (
                  <div className={classes.imCelticsButtonsWrapper}>
                    <Button
                      onClick={(): void => setSubmitModalOpen(true)}
                      color="custom"
                      customColor="purple"
                      type="outlined"
                      size="large"
                      width="24rem"
                    >
                      Re-Submit
                    </Button>
                    <p className={classes.imCelticsDisclaimer}>Button available only for Underwriters</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <Button
                  onClick={(): void => setSubmitModalOpen(true)}
                  disabled={isSubmitNotEligible || !isElectionValid || isFinalRatesMissing}
                  color="primary"
                  size="large"
                  width="24rem"
                >
                  Submit
                </Button>
                {isUwRights && !isFinalRatesMissing && (isSubmitNotEligible || !isElectionValid) && (
                  <div className={classes.imCelticsButtonsWrapper}>
                    <Button
                      onClick={(): void => setSubmitModalOpen(true)}
                      color="custom"
                      customColor="purple"
                      type="outlined"
                      size="large"
                      width="24rem"
                    >
                      Override Rules & Submit
                    </Button>
                    <p className={classes.imCelticsDisclaimer}>Button available only for Underwriters</p>
                  </div>
                )}
              </>
            )}
          </div>
          <ActionModal
            show={submitModalOpen}
            onClose={(): void => setSubmitModalOpen(false)}
            onAction={completeEnrollmentPeriod}
            title="Submit Enrollment"
            subtitle={
              isAlreadySubmitted ? (
                <span>
                  An enrollment packet for this effective date has already been generated. Selecting submit again will
                  regenerate a new enrollment packet with any changes you have made.
                </span>
              ) : (
                <span>
                  By selecting submit, you will generate an enrollment packet for this group for the selected plans and
                  effective date. Please note that it may take up to 30 seconds to generate all of the pdfs for this
                  enrollment packet.
                </span>
              )
            }
            closeButtonText="Cancel"
            actionButtonText="Submit Enrollment"
          />
        </>
      )}
    </>
  );
});

export default ReviewSubmitFooter;
